import {
  asText,
  toDate,
} from "./despatchWarehouseReportHelper";

export { asText, toDate };

function snapshotData(document) {
  if (!document) return {};
  if (typeof document.data === "function") return document.data() || {};
  if (document.data && typeof document.data === "object") return document.data;
  return document;
}

function snapshotId(document) {
  return asText(document?.id);
}

export function normalizeReport(document) {
  const data = snapshotData(document);
  return {
    id: snapshotId(document),
    reportNumber: asText(data.reportNumber),
    createdAt: toDate(data.createdAt),
    completed: data.completed === true,
    _raw: data,
  };
}

export function normalizeDetailItems(document) {
  const data = snapshotData(document);
  const items = Array.isArray(data.items) ? data.items : [];

  return items.map((item, index) => ({
    id: asText(item?.id) || `item-${index}`,
    barcode: asText(item?.barcode),
    serialNumber: asText(item?.serialNumber),
    productId: asText(item?.productId),
    productCode: asText(item?.productCode),
    special: asText(item?.special),
    scannedAt: toDate(item?.scannedAt),
  }));
}

export function localDateKey(value) {
  const date = toDate(value);
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function availableDateKeys(reports) {
  return new Set(
    (reports || [])
      .map((report) => localDateKey(report.createdAt))
      .filter(Boolean)
  );
}

export function filterReportsByDateKey(reports, dateKey) {
  const normalizedDateKey = asText(dateKey);
  if (!normalizedDateKey) return [...(reports || [])];
  return (reports || []).filter(
    (report) => localDateKey(report.createdAt) === normalizedDateKey
  );
}

export function matchReportNumber(report, term) {
  const normalizedTerm = asText(term).toLowerCase();
  const reportNumber = asText(report?.reportNumber).toLowerCase();
  if (!normalizedTerm) return { matches: true, exact: false };
  return {
    matches: reportNumber.includes(normalizedTerm),
    exact: reportNumber === normalizedTerm,
  };
}

export function matchesSerial(item, term) {
  const normalizedTerm = asText(term).toLowerCase();
  const serialNumber = asText(item?.serialNumber).toLowerCase();
  if (!normalizedTerm) return { matches: false, exact: false };
  return {
    matches: serialNumber.includes(normalizedTerm),
    exact: serialNumber === normalizedTerm,
  };
}

export function findReportsBySerial({ reports, detailsById, term }) {
  const exact = [];
  const partial = [];
  const missingDetailIds = [];
  const matchesByReportId = new Map();

  for (const report of reports || []) {
    if (!(detailsById instanceof Map) || !detailsById.has(report.id)) {
      missingDetailIds.push(report.id);
      continue;
    }

    const matchingItems = [];
    let hasExact = false;
    for (const item of detailsById.get(report.id) || []) {
      const result = matchesSerial(item, term);
      if (!result.matches) continue;
      matchingItems.push(item);
      if (result.exact) hasExact = true;
    }

    if (!matchingItems.length) continue;
    matchesByReportId.set(report.id, matchingItems);
    const match = {
      ...report,
      _serialMatches: matchingItems,
      _serialMatchExact: hasExact,
    };
    (hasExact ? exact : partial).push(match);
  }

  return {
    reports: [...exact, ...partial],
    matchesByReportId,
    missingDetailIds,
  };
}

function duplicateGroups(entries) {
  return [...entries.entries()]
    .filter(([, ids]) => ids.size > 1)
    .map(([value, ids]) => ({
      value,
      reportIds: [...ids],
    }));
}

export function reconcileReportPresence(reports, detailsById) {
  const reportIds = new Set((reports || []).map((report) => report.id));
  const detailsLoaded = detailsById instanceof Map;
  const detailIds = detailsLoaded ? new Set(detailsById.keys()) : new Set();
  const reportNumbers = new Map();

  for (const report of reports || []) {
    if (!report.reportNumber) continue;
    if (!reportNumbers.has(report.reportNumber)) {
      reportNumbers.set(report.reportNumber, new Set());
    }
    reportNumbers.get(report.reportNumber).add(report.id);
  }

  // NOTE: a serial number appearing in more than one report is normal and
  // expected (cylinders are legitimately despatched more than once), so
  // cross-report serial repetition is intentionally NOT treated as an
  // integrity issue.
  return {
    summaryWithoutDetail: detailsLoaded
      ? [...reportIds].filter((id) => !detailIds.has(id))
      : [],
    detailWithoutSummary: detailsLoaded
      ? [...detailIds].filter((id) => !reportIds.has(id))
      : [],
    duplicateReportNumbers: duplicateGroups(reportNumbers),
  };
}

export function formatReportDate(value) {
  const date = toDate(value);
  return date ? date.toLocaleDateString("en-GB") : "";
}
